import { string } from '@calendly/form/validation'

import { RECAP_RELATIONS } from 'features/recaps/constants'
import { getGroup } from 'features/user_groups'
import { fetchOrganizationMembershipsByUserUuids } from 'shared/api/organization_memberships'

import { PICKER_ENTER_RESULT_KIND, PERMISSION_SUBJECT_TYPES, RECIPIENT_KINDS } from './constants'

const RECIPIENT_SUGGESTION_TYPES = Object.freeze({
  ORGANIZATION_MEMBERSHIP: 'organization_membership',
  USER_GROUP: 'user_group',
})

const getTupleSubjectType = (tuple) => tuple.subjectType ?? tuple.subject_type

const getTupleSubjectId = (tuple) => tuple.subjectId ?? tuple.subject_id

// Parses GET /permissions: legacy bare tuple array (user has internal recap sharing only),
//  or enriched { internal, external, pendingInvites }.
export const normalizeRecapPermissionsResponse = (raw) => {
  // Handle legacy bare tuple array (user has internal recap sharing only), it should return the array as is.
  if (Array.isArray(raw)) {
    return raw
  }

  // Handle the enriched object
  if (raw && typeof raw === 'object') {
    return {
      internal: Array.isArray(raw.internal) ? raw.internal : [],
      external: Array.isArray(raw.external) ? raw.external : [],
      pendingInvites: Array.isArray(raw.pendingInvites) ? raw.pendingInvites : [],
    }
  }

  return []
}

// Internal SpiceDB tuples from a normalized or legacy permissions response.
export const getInternalPermissionTuples = (normalized) => {
  if (normalized == null) {
    return []
  }

  if (Array.isArray(normalized)) {
    return normalized
  }

  return Array.isArray(normalized.internal) ? normalized.internal : []
}

// External viewer rows from an enriched permissions response.
export const getExternalPermissionRows = (normalized) => {
  if (normalized == null || Array.isArray(normalized)) {
    return []
  }

  return Array.isArray(normalized.external) ? normalized.external : []
}

// Pending external invite rows from an enriched permissions response.
export const getPendingInviteRows = (normalized) => {
  if (normalized == null || Array.isArray(normalized)) {
    return []
  }

  return Array.isArray(normalized.pendingInvites) ? normalized.pendingInvites : []
}

// Flag OFF discards external/pending on enriched object responses; bare arrays pass through unchanged.
export const applyExternalRecapSharingGate = (normalized, hasExternalRecapSharing) => {
  if (Array.isArray(normalized) || hasExternalRecapSharing) {
    return normalized
  }

  return {
    ...normalized,
    external: [],
    pendingInvites: [],
  }
}

export const splitRecapPermissionTuples = (tuples) => {
  if (!Array.isArray(tuples)) {
    return { users: [], userGroups: [], organizations: [] }
  }

  return tuples.reduce((acc, tuple) => {
    const subjectType = getTupleSubjectType(tuple)
    const subjectId = getTupleSubjectId(tuple)

    if (!subjectId) {
      return acc
    }

    const entry = {
      uuid: subjectId,
      relation: tuple.relation,
    }

    if (subjectType === PERMISSION_SUBJECT_TYPES.USER) {
      acc.users.push(entry)
    } else if (subjectType === PERMISSION_SUBJECT_TYPES.USER_GROUP) {
      acc.userGroups.push(entry)
    } else if (subjectType === PERMISSION_SUBJECT_TYPES.ORGANIZATION) {
      acc.organizations.push(entry)
    }

    return acc
  }, { users: [], userGroups: [], organizations: [] })
}

export const countNonOwnerUserGrants = (users, ownerUserUuid) => (
  users.filter(({ uuid }) => uuid !== ownerUserUuid).length
)

export const countShareRecipients = (
  users,
  userGroups,
  ownerUserUuid,
  { externalUsers = [], pendingInvites = [] } = {},
) => (
  countNonOwnerUserGrants(users, ownerUserUuid)
  + userGroups.reduce((total, { memberCount = 0 }) => total + memberCount, 0)
  + externalUsers.length
  + pendingInvites.length
)

export const deriveExternalAvatarLetter = (name, email) => {
  const trimmedName = name?.trim()

  if (trimmedName) {
    return trimmedName[0]?.toUpperCase() ?? null
  }

  const trimmedEmail = email?.trim()

  if (!trimmedEmail || /^\d/.test(trimmedEmail)) {
    return null
  }

  return trimmedEmail[0]?.toUpperCase() ?? null
}

// Maps GET /permissions external rows to access-list display shape.
export const mapExternalUsersToAccessListRows = (externalUsers) => {
  if (!Array.isArray(externalUsers)) {
    return []
  }

  return externalUsers.reduce((rows, externalUser) => {
    // API returns subjectId (SpiceDB tuple shape)
    const subjectUuid = externalUser.subjectId

    if (!subjectUuid) {
      return rows
    }

    const email = externalUser.email ?? ''
    const name = externalUser.name ?? ''

    rows.push({
      uuid: subjectUuid,
      email,
      name,
      avatarUrl: externalUser.avatarUrl ?? externalUser.avatar_url ?? null,
      avatarLetter: deriveExternalAvatarLetter(name, email),
      relation: externalUser.relation ?? RECAP_RELATIONS.EXTERNAL_VIEWER,
      isExternal: true,
    })

    return rows
  }, [])
}

// Maps GET /permissions pendingInvites rows to access-list display shape.
export const mapPendingInvitesToAccessListRows = (pendingInvites) => {
  if (!Array.isArray(pendingInvites)) {
    return []
  }

  return pendingInvites.reduce((rows, pendingInvite) => {
    const email = pendingInvite.email

    if (!email) {
      return rows
    }

    rows.push({
      uuid: null, // No uuid because pending invites don't have an account
      email: email ?? '',
      name: email ?? '',
      avatarUrl: null,
      avatarLetter: deriveExternalAvatarLetter('', email),
      relation: pendingInvite.relation ?? RECAP_RELATIONS.EXTERNAL_VIEWER,
      isPendingInvite: true,
    })

    return rows
  }, [])
}

// TODO external sharing: need to add userEmails arg too
export const hasPendingPermissionChanges = (pendingPermissions) => (
  Object.keys(pendingPermissions.users).length > 0
  || Object.keys(pendingPermissions.userGroups).length > 0
  || Object.keys(pendingPermissions.userEmails).length > 0
  || pendingPermissions.organization !== undefined
)

const getGroupSubjectId = (userGroup) => userGroup.uuid ?? String(userGroup.id)

const normalizeEmailAddress = (email) => email.trim().toLowerCase()

// Email validation for picker chips and exclusion checks.
const isValidEmailAddress = (email) => string().email().isValidSync(email.trim())

// Builds a pending user_email chip from raw picker input.
export const emailToPendingRecipient = (email) => {
  const normalizedEmail = normalizeEmailAddress(email)

  return {
    id: `${RECIPIENT_KINDS.USER_EMAIL}-${normalizedEmail}`,
    kind: RECIPIENT_KINDS.USER_EMAIL,
    email: normalizedEmail,
    relation: RECAP_RELATIONS.EXTERNAL_VIEWER,
    isValidEmail: isValidEmailAddress(normalizedEmail),
    display: {
      name: '',
      email: normalizedEmail,
      avatarUrl: null,
      avatarLetter: deriveExternalAvatarLetter('', normalizedEmail),
    },
  }
}

// Finds an org membership suggestion whose email exactly matches the query.
export const findSuggestionWithExactEmail = (suggestions, email) => {
  const normalizedEmail = normalizeEmailAddress(email)

  if (!normalizedEmail) {
    return null
  }

  return suggestions.find((suggestion) => (
    suggestion.type === RECIPIENT_SUGGESTION_TYPES.ORGANIZATION_MEMBERSHIP
    && suggestion.entity.email
    && normalizeEmailAddress(suggestion.entity.email) === normalizedEmail
  )) ?? null
}

// Resolves Enter-key input to an internal suggestion or external email chip.
export const resolvePickerInputOnEnter = ({
  query,
  suggestions,
  isSearching,
  hasExternalRecapSharing,
}) => {
  const trimmed = query.trim()

  if (!trimmed) {
    return null
  }

  // 1. If the user entered an email and there is an internal user (within the same org)
  // that exactly matches the email, then select that user.
  const exactMatch = findSuggestionWithExactEmail(suggestions, trimmed)

  if (exactMatch) {
    return { kind: PICKER_ENTER_RESULT_KIND.SUGGESTION, suggestion: exactMatch }
  }

  // 2. If the user entered an email address, then select that email.
  if (hasExternalRecapSharing && isValidEmailAddress(trimmed)) {
    return { kind: PICKER_ENTER_RESULT_KIND.EMAIL, recipient: emailToPendingRecipient(trimmed) }
  }

  // 3. If the user entered a non-email and it matches with a suggestion in the dropdown, select the first suggestion.
  if (suggestions.length > 0 && !isSearching) {
    return { kind: PICKER_ENTER_RESULT_KIND.SUGGESTION, suggestion: suggestions[0] }
  }

  // 4. There are no suggestions in the dropdown, and the user did not enter an email.
  // So what the user typed will show as an invalid email.
  if (hasExternalRecapSharing && (isSearching || suggestions.length === 0)) {
    return { kind: PICKER_ENTER_RESULT_KIND.EMAIL, recipient: emailToPendingRecipient(trimmed) }
  }

  return null
}

export const suggestionToPendingRecipient = (suggestion) => {
  if (suggestion.type === RECIPIENT_SUGGESTION_TYPES.ORGANIZATION_MEMBERSHIP) {
    const { entity } = suggestion
    const userUuid = entity.user_uuid

    if (!userUuid) {
      return null
    }

    return {
      id: suggestion.id,
      kind: RECIPIENT_KINDS.USER,
      uuid: userUuid,
      relation: RECAP_RELATIONS.VIEWER,
      display: {
        name: entity.name ?? '',
        email: entity.email ?? '',
        avatarUrl: entity.avatar_url ?? null,
        avatarLetter: entity.avatar_letter ?? null,
      },
    }
  }

  if (suggestion.type === RECIPIENT_SUGGESTION_TYPES.USER_GROUP) {
    const { entity } = suggestion
    const groupUuid = getGroupSubjectId(entity)

    if (!groupUuid) {
      return null
    }

    return {
      id: suggestion.id,
      kind: RECIPIENT_KINDS.USER_GROUP,
      uuid: groupUuid,
      relation: RECAP_RELATIONS.VIEWER,
      display: {
        name: entity.name ?? '',
        memberCount: entity.user_group_relationships_count ?? 0,
      },
    }
  }

  return null
}

export const buildNewRecipientsPayload = (pendingRecipients) => ({
  users: pendingRecipients
    .filter(({ kind }) => kind === RECIPIENT_KINDS.USER)
    .map(({ uuid, relation }) => ({ uuid, relation })),
  userGroups: pendingRecipients
    .filter(({ kind }) => kind === RECIPIENT_KINDS.USER_GROUP)
    .map(({ uuid, relation }) => ({ uuid, relation })),
  userEmails: pendingRecipients
    .filter(({ kind, isValidEmail }) => (
      kind === RECIPIENT_KINDS.USER_EMAIL && isValidEmail !== false
    ))
    .map(({ email, relation }) => ({ email, relation })),
})

// Look up a user group's existing server-side relation from the access list.
const findUserGroupServerRelation = (accessList, uuid) => (
  accessList.userGroups?.find((entry) => entry.uuid === uuid)?.relation
  ?? RECAP_RELATIONS.VIEWER
)

// Look up a user's existing server-side relation from the access list.
// Same-org users only; external users revoke via userEmails.
const findUserServerRelation = (accessList, uuid) => (
  accessList.internalUsers?.find((entry) => entry.uuid === uuid)?.relation
  ?? RECAP_RELATIONS.VIEWER
)

// Look up an email subject's existing server-side relation (external user first,
// then pending invite). Both revoke via userEmails with external_viewer.
const findUserEmailServerRelation = (accessList, email) => {
  const externalRelation = accessList.externalUsers?.find((entry) => entry.email === email)?.relation

  if (externalRelation != null) {
    return externalRelation
  }

  const pendingRelation = accessList.pendingInvites?.find((entry) => entry.email === email)?.relation

  if (pendingRelation != null) {
    return pendingRelation
  }

  return RECAP_RELATIONS.EXTERNAL_VIEWER
}

// Split pending edits into a POST payload (new recipients + relation grants/changes) and a
// DELETE payload (removals). Removals send the original server relation for the tuple.
export const buildPermissionSubmissionPayloads = ({
  pendingPermissionUpdates,
  pendingRecipients,
  accessList,
}) => {
  const newRecipients = buildNewRecipientsPayload(pendingRecipients)
  const postPayload = {
    users: [...newRecipients.users],
    userGroups: [...newRecipients.userGroups],
    organizations: [],
    userEmails: [...newRecipients.userEmails],
  }
  const deletePayload = {
    users: [],
    userGroups: [],
    organizations: [],
    userEmails: [],
  }

  Object.entries(pendingPermissionUpdates.users ?? {}).forEach(([uuid, relation]) => {
    if (relation === null) {
      deletePayload.users.push({ uuid, relation: findUserServerRelation(accessList, uuid) })
    } else {
      postPayload.users.push({ uuid, relation })
    }
  })

  Object.entries(pendingPermissionUpdates.userEmails ?? {}).forEach(([email, relation]) => {
    if (relation === null) {
      deletePayload.userEmails.push({
        email,
        relation: findUserEmailServerRelation(accessList, email),
      })
    } else {
      postPayload.userEmails.push({ email, relation })
    }
  })

  Object.entries(pendingPermissionUpdates.userGroups ?? {}).forEach(([uuid, relation]) => {
    if (relation === null) {
      deletePayload.userGroups.push({ uuid, relation: findUserGroupServerRelation(accessList, uuid) })
    } else {
      postPayload.userGroups.push({ uuid, relation })
    }
  })

  const orgRelation = pendingPermissionUpdates.organization
  const organizationUuid = accessList.organization?.uuid

  if (orgRelation !== undefined && organizationUuid != null) {
    const orgUuid = String(organizationUuid)

    if (orgRelation === null) {
      deletePayload.organizations.push({
        uuid: orgUuid,
        relation: accessList.organization?.relation ?? RECAP_RELATIONS.VIEWER,
      })
    } else {
      postPayload.organizations.push({ uuid: orgUuid, relation: orgRelation })
    }
  }

  const postUserUuids = new Set(postPayload.users.map(({ uuid }) => uuid))
  const postGroupUuids = new Set(postPayload.userGroups.map(({ uuid }) => uuid))
  const postOrgUuids = new Set(postPayload.organizations.map(({ uuid }) => uuid))
  const postUserEmails = new Set(postPayload.userEmails.map(({ email }) => email))

  deletePayload.users = deletePayload.users.filter(({ uuid }) => !postUserUuids.has(uuid))
  deletePayload.userGroups = deletePayload.userGroups.filter(({ uuid }) => !postGroupUuids.has(uuid))
  deletePayload.organizations = deletePayload.organizations.filter(({ uuid }) => !postOrgUuids.has(uuid))
  deletePayload.userEmails = deletePayload.userEmails.filter(({ email }) => !postUserEmails.has(email))

  return { postPayload, deletePayload }
}

export const isPermissionPayloadEmpty = (payload) => (
  payload.users.length === 0
  && payload.userGroups.length === 0
  && payload.organizations.length === 0
  && (payload.userEmails?.length ?? 0) === 0
)

const buildUserRowFromPendingRecipient = (recipient, relation) => ({
  uuid: recipient.uuid,
  relation,
  name: recipient.display.name ?? '',
  email: recipient.display.email ?? '',
  avatarUrl: recipient.display.avatarUrl ?? null,
  avatarLetter: recipient.display.avatarLetter ?? null,
})

const buildGroupRowFromPendingRecipient = (recipient, relation) => ({
  uuid: recipient.uuid,
  relation,
  name: recipient.display.name ?? '',
  memberCount: recipient.display.memberCount ?? 0,
})

const upsertAccessListUser = (users, { uuid, relation }, recipientsAtSubmit) => {
  const existingIndex = users.findIndex((user) => user.uuid === uuid)

  if (existingIndex >= 0) {
    const nextUsers = [...users]
    nextUsers[existingIndex] = { ...nextUsers[existingIndex], relation }
    return nextUsers
  }

  const recipientAtSubmit = recipientsAtSubmit.find(
    (recipient) => recipient.kind === RECIPIENT_KINDS.USER && recipient.uuid === uuid,
  )

  if (recipientAtSubmit) {
    return [...users, buildUserRowFromPendingRecipient(recipientAtSubmit, relation)]
  }

  return [...users, {
    uuid,
    relation,
    name: '',
    email: '',
    avatarUrl: null,
    avatarLetter: null,
  }]
}

const upsertAccessListUserGroup = (userGroups, { uuid, relation }, recipientsAtSubmit) => {
  const existingIndex = userGroups.findIndex((userGroup) => userGroup.uuid === uuid)

  if (existingIndex >= 0) {
    const nextUserGroups = [...userGroups]
    nextUserGroups[existingIndex] = { ...nextUserGroups[existingIndex], relation }
    return nextUserGroups
  }

  const recipientAtSubmit = recipientsAtSubmit.find(
    (recipient) => (
      recipient.kind === RECIPIENT_KINDS.USER_GROUP && recipient.uuid === uuid
    ),
  )

  if (recipientAtSubmit) {
    return [...userGroups, buildGroupRowFromPendingRecipient(recipientAtSubmit, relation)]
  }

  return [...userGroups, {
    uuid,
    relation,
    name: '',
    memberCount: 0,
  }]
}

const buildPendingInviteRowFromEmail = (email, relation, recipientsAtSubmit) => {
  const recipientAtSubmit = recipientsAtSubmit.find(
    (recipient) => (
      recipient.kind === RECIPIENT_KINDS.USER_EMAIL && recipient.email === email
    ),
  )

  return {
    uuid: null,
    email,
    name: recipientAtSubmit?.display?.email ?? email,
    avatarUrl: recipientAtSubmit?.display?.avatarUrl ?? null,
    avatarLetter: recipientAtSubmit?.display?.avatarLetter
      ?? deriveExternalAvatarLetter('', email),
    relation: relation ?? RECAP_RELATIONS.EXTERNAL_VIEWER,
    isPendingInvite: true,
  }
}

const upsertAccessListPendingInvite = (pendingInvites, { email, relation }, recipientsAtSubmit) => {
  const existingIndex = pendingInvites.findIndex((invite) => invite.email === email)

  if (existingIndex >= 0) {
    const nextPendingInvites = [...pendingInvites]
    nextPendingInvites[existingIndex] = {
      ...nextPendingInvites[existingIndex],
      relation: relation ?? nextPendingInvites[existingIndex].relation,
    }
    return nextPendingInvites
  }

  return [...pendingInvites, buildPendingInviteRowFromEmail(email, relation, recipientsAtSubmit)]
}

export const mergePostPermissionsIntoAccessList = (accessList, postResponse, recipientsAtSubmit = []) => {
  let { internalUsers, userGroups, organization, pendingInvites = [] } = accessList;

  (postResponse?.users ?? []).forEach((entry) => {
    internalUsers = upsertAccessListUser(internalUsers, entry, recipientsAtSubmit)
  });

  (postResponse?.userGroups ?? []).forEach((entry) => {
    userGroups = upsertAccessListUserGroup(userGroups, entry, recipientsAtSubmit)
  });

  (postResponse?.organizations ?? []).forEach(({ uuid, relation }) => {
    if (organization && String(organization.uuid) === String(uuid)) {
      organization = { ...organization, relation }
    }
  });

  (postResponse?.userEmails ?? []).forEach((entry) => {
    pendingInvites = upsertAccessListPendingInvite(pendingInvites, entry, recipientsAtSubmit)
  })

  return {
    ...accessList,
    internalUsers,
    userGroups,
    organization,
    pendingInvites,
  }
}

export const mergeDeletePermissionsIntoAccessList = (accessList, deletePayload) => {
  const removedUserUuids = new Set((deletePayload?.users ?? []).map(({ uuid }) => uuid))
  const removedGroupUuids = new Set((deletePayload?.userGroups ?? []).map(({ uuid }) => uuid))
  const removedOrgUuids = new Set((deletePayload?.organizations ?? []).map(({ uuid }) => String(uuid)))
  const removedUserEmails = new Set((deletePayload?.userEmails ?? []).map(({ email }) => email))

  let { internalUsers, userGroups, organization, externalUsers, pendingInvites } = accessList

  internalUsers = internalUsers.filter((user) => !removedUserUuids.has(user.uuid))
  userGroups = userGroups.filter((userGroup) => !removedGroupUuids.has(userGroup.uuid))
  externalUsers = (externalUsers ?? []).filter((user) => !removedUserEmails.has(user.email))
  pendingInvites = (pendingInvites ?? []).filter((invite) => !removedUserEmails.has(invite.email))

  if (organization && removedOrgUuids.has(String(organization.uuid))) {
    organization = { ...organization, relation: null }
  }

  return {
    ...accessList,
    internalUsers,
    userGroups,
    organization,
    externalUsers,
    pendingInvites,
  }
}

const countPendingRecipients = (pendingRecipients) => (
  pendingRecipients.reduce((total, recipient) => {
    if (recipient.kind === RECIPIENT_KINDS.USER_GROUP) {
      return total + (recipient.display.memberCount ?? 0)
    }

    return total + 1
  }, 0)
)

export const countEffectiveRecipients = ({
  users,
  userGroups,
  ownerUserUuid,
  pendingRecipients,
  externalUsers = [],
  pendingInvites = [],
}) => (
  countShareRecipients(users, userGroups, ownerUserUuid, { externalUsers, pendingInvites })
  + countPendingRecipients(pendingRecipients)
)

const addNormalizedEmailsToSet = (targetSet, emails = []) => {
  emails.forEach((email) => {
    const normalizedEmail = email?.trim().toLowerCase()

    if (normalizedEmail) {
      targetSet.add(normalizedEmail)
    }
  })
}

export const buildSuggestionExclusion = ({
  pendingRecipients,
  accessListUsers,
  accessListUserGroups,
  accessListExternalUsers = [],
  accessListPendingInvites = [],
  getUserRelation,
  getUserGroupRelation,
  ownerUserUuid,
}) => {
  const excludedChipIds = new Set(pendingRecipients.map(({ id }) => id))
  const excludedUserUuids = new Set(
    pendingRecipients
      .filter(({ kind }) => kind === RECIPIENT_KINDS.USER)
      .map(({ uuid }) => uuid),
  )
  const excludedGroupUuids = new Set(
    pendingRecipients
      .filter(({ kind }) => kind === RECIPIENT_KINDS.USER_GROUP)
      .map(({ uuid }) => uuid),
  )
  const excludedEmails = new Set(
    pendingRecipients
      .filter(({ kind }) => kind === RECIPIENT_KINDS.USER_EMAIL)
      .map(({ email }) => email),
  )

  if (ownerUserUuid) {
    excludedUserUuids.add(ownerUserUuid)
  }

  accessListUsers.forEach((user) => {
    if (getUserRelation(user.uuid, user.relation) !== null) {
      excludedUserUuids.add(user.uuid)
    }
  })

  accessListUserGroups.forEach((userGroup) => {
    if (getUserGroupRelation(userGroup.uuid, userGroup.relation) !== null) {
      excludedGroupUuids.add(userGroup.uuid)
    }
  })

  addNormalizedEmailsToSet(
    excludedEmails,
    accessListExternalUsers.map(({ email }) => email),
  )
  addNormalizedEmailsToSet(
    excludedEmails,
    accessListPendingInvites.map(({ email }) => email),
  )

  return { excludedChipIds, excludedUserUuids, excludedGroupUuids, excludedEmails }
}

const isSuggestionExcluded = (suggestion, exclusion) => {
  if (exclusion.excludedChipIds.has(suggestion.id)) {
    return true
  }

  if (suggestion.type === RECIPIENT_SUGGESTION_TYPES.ORGANIZATION_MEMBERSHIP) {
    const userUuid = suggestion.entity.user_uuid

    return Boolean(userUuid && exclusion.excludedUserUuids.has(userUuid))
  }

  if (suggestion.type === RECIPIENT_SUGGESTION_TYPES.USER_GROUP) {
    const groupUuid = getGroupSubjectId(suggestion.entity)

    return Boolean(groupUuid && exclusion.excludedGroupUuids.has(groupUuid))
  }

  return false
}

export const filterExcludedSuggestions = (suggestions, exclusion) => (
  suggestions.filter((suggestion) => !isSuggestionExcluded(suggestion, exclusion))
)

// Display the user/group's pending relation if it exists
export const relationToDisplay = (pendingMap, uuid, serverRelation) => (
  Object.hasOwn(pendingMap, uuid) ? pendingMap[uuid] : serverRelation
)

// Display the organization's pending relation if it exists
export const organizationRelationToDisplay = (pendingRelation, serverRelation) => (
  pendingRelation !== undefined ? pendingRelation : serverRelation
)

export const buildAccessListOrganization = (orgUuid, organizations) => {
  if (!orgUuid) {
    return null
  }

  const currentOrganizationGrant = organizations.find(({ uuid }) => String(uuid) === String(orgUuid)) ?? null

  return currentOrganizationGrant ? {
    uuid: currentOrganizationGrant.uuid,
    relation: currentOrganizationGrant.relation
  } : { uuid: orgUuid, relation: null }
}

const mapUserOrgMembershipToAccessListUser = (userOrgMembership, { uuid, relation, isCurrentUser = false }) => ({
  uuid,
  relation,
  name: userOrgMembership?.name ?? uuid,
  email: userOrgMembership?.email ?? '',
  avatarUrl: userOrgMembership?.avatar_url ?? null,
  // Use first letter of name or email, since avatar_letter is not available in the organization membership
  avatarLetter: userOrgMembership?.name?.[0]?.toUpperCase() ?? userOrgMembership?.email?.[0]?.toUpperCase() ?? null,
  isCurrentUser,
})

const loadMembershipsByUserUuid = async (userUuids) => {
  if (!userUuids.length) {
    return {}
  }

  try {
    const memberships = await fetchOrganizationMembershipsByUserUuids(userUuids)

    return memberships.reduce((acc, membership) => {
      if (membership.user_uuid) {
        acc[membership.user_uuid] = membership
      }

      return acc
    }, {})
  } catch {
    return {}
  }
}

// currentUser object is from the current user's profile, not the organization membership
const resolveAccessListOwner = ({ owner, currentUser }) => {
  const ownerUserUuid = owner?.uuid

  if (!ownerUserUuid) {
    return null
  }

  if (currentUser?.uuid === ownerUserUuid) {
    return {
      uuid: ownerUserUuid,
      relation: RECAP_RELATIONS.OWNER,
      name: currentUser.profile?.name ?? currentUser.name,
      email: currentUser.email,
      avatarUrl: currentUser.avatar_url,
      avatarLetter: currentUser.avatar_letter,
      isCurrentUser: true,
    }
  }

  if (owner.displayName || owner.name || owner.email) {
    const name = owner.name ?? owner.displayName ?? null

    return {
      uuid: ownerUserUuid,
      relation: RECAP_RELATIONS.OWNER,
      name: name ?? ownerUserUuid,
      email: owner.email ?? '',
      avatarUrl: owner.avatarUrl ?? null,
      avatarLetter: owner.avatarLetter ?? null,
      isCurrentUser: false,
    }
  }

  return mapUserOrgMembershipToAccessListUser(null, {
    uuid: ownerUserUuid,
    relation: RECAP_RELATIONS.OWNER,
    isCurrentUser: false,
  })
}

export const enrichAccessListUsers = async (_dispatch, users, owner, currentUser) => {
  const ownerUserUuid = owner?.uuid
  const nonOwnerUsers = users.filter(({ uuid }) => uuid !== ownerUserUuid)
  const membershipsByUserUuid = await loadMembershipsByUserUuid(
    nonOwnerUsers.map(({ uuid }) => uuid),
  )

  return {
    owner: resolveAccessListOwner({ owner, currentUser }),
    users: nonOwnerUsers.map(({ uuid, relation }) => (
      mapUserOrgMembershipToAccessListUser(membershipsByUserUuid[uuid] ?? null, { uuid, relation })
    )),
  }
}

export const enrichAccessListUserGroups = async (userGroups) => {
  const enrichedGroups = await Promise.all(
    userGroups.map(async ({ uuid, relation }) => {
      const [success, response] = await getGroup(uuid)
      if (!success) {
        return {
          uuid,
          relation,
          name: '',
          memberCount: 0,
          forbidden: true,
        }
      }

      const memberCount = response?.user_group_relationships_count ?? 0
      return {
        uuid,
        relation,
        name: response?.name ?? '',
        memberCount,
        forbidden: false,
      }
    }),
  )

  return enrichedGroups
}
